import { useEffect, useMemo, useState } from 'react';
import type {
  DrawAllocationMode,
  DrawPreview,
  OddPlayerPolicy,
  PairingPriority,
  PlayEvent,
  PlayEventParticipant,
} from '@court-sync/shared';
import {
  DEFAULT_PAIRING_PRIORITIES,
  ODD_PLAYER_POLICIES,
  flattenDrawPreviewMatches,
  getPlayEventParticipantDisplayName,
  resolvePlayEventMatchSideCapacity,
} from '@court-sync/shared';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import toast from 'react-hot-toast';
import { RefreshCw, Wand2 } from 'lucide-react';
import {
  Button,
  Drawer,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui';
import { PlayEventPairingPrioritiesField } from '@/components/play/PlayEventPairingPrioritiesField';
import {
  PlayEventDrawMatchList,
  playEventDrawMatchListDragHandler,
  type DrawMatchListItem,
} from '@/components/play/PlayEventDrawMatchList';
import { PlayEventBracketTree } from '@/components/play/PlayEventBracketTree';
import { PlayEventDrawReviewDiagram } from '@/components/play/PlayEventDrawReviewDiagram';
import { PlayEventDrawPlayerDragGhost } from '@/components/play/PlayEventDrawPlayerSlot';
import {
  assignPreviewParticipantToSlot,
  buildAutoCourtAssignmentsForPreview,
  buildDrawGeneratePayload,
  buildEventDrawPreview,
  buildPreviewDrawMatchListItems,
  getAvailableParticipantsForPreviewRound,
  getEligibleDrawParticipants,
  getPreviewRound,
  swapPreviewParticipants,
} from '@/lib/playEventDrawUtils';
import { snapCenterToCursor } from '@/lib/playEventDrawDnd';
import { playEventService } from '@/services/playEvent';
import { clubService } from '@/services/club';
import { cn } from '@/lib/utils';

const ODD_PLAYER_POLICY_LABELS: Record<OddPlayerPolicy, string> = {
  sit_out: 'Sit out',
  fallback_singles: 'Fallback singles',
  american_singles: 'American singles',
  fallback_doubles_sit_out: 'Doubles sit out',
};

interface PlayEventDrawSetupWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  eventId: string;
  event: PlayEvent;
  participants: PlayEventParticipant[];
  onCompleted: () => void;
}

type WizardStep = 1 | 2 | 3;
type PreviewTab = 'round' | 'bracket';

export function PlayEventDrawSetupWizard({
  open,
  onOpenChange,
  organizationId,
  eventId,
  event,
  participants,
  onCompleted,
}: PlayEventDrawSetupWizardProps) {
  const [step, setStep] = useState<WizardStep>(1);
  const [allocationMode, setAllocationMode] = useState<DrawAllocationMode>('auto');
  const [roundCount, setRoundCount] = useState(String(event.roundCount ?? 4));
  const [roundDurationMinutes, setRoundDurationMinutes] = useState(
    String(event.roundDurationMinutes ?? 30)
  );
  const [roundBreakMinutes, setRoundBreakMinutes] = useState(
    String(event.roundBreakMinutes ?? 0)
  );
  const [pairingPriorities, setPairingPriorities] = useState<PairingPriority[]>(
    event.pairingPriorities ?? DEFAULT_PAIRING_PRIORITIES
  );
  const [oddPlayerPolicy, setOddPlayerPolicy] = useState<OddPlayerPolicy>(
    event.oddPlayerPolicy ?? 'sit_out'
  );
  const [preview, setPreview] = useState<DrawPreview | null>(null);
  const [previewTab, setPreviewTab] = useState<PreviewTab>('round');
  const [selectedRoundNumber, setSelectedRoundNumber] = useState<number>(1);
  const [submitting, setSubmitting] = useState(false);
  const [draggingParticipantId, setDraggingParticipantId] = useState<string | null>(null);
  const [courtAssignmentsByKey, setCourtAssignmentsByKey] = useState<
    Record<string, string | null>
  >({});
  const [pairingShuffleSeed, setPairingShuffleSeed] = useState(0);
  const [courtNamesById, setCourtNamesById] = useState<Record<string, string>>({});

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    })
  );

  const eligibleParticipants = useMemo(
    () => getEligibleDrawParticipants(event, participants),
    [event, participants]
  );

  const participantsById = useMemo(
    () => new Map(eligibleParticipants.map((participant) => [participant.id, participant])),
    [eligibleParticipants]
  );

  const participantNamesById = useMemo(
    () =>
      Object.fromEntries(
        eligibleParticipants.map((participant) => [
          participant.id,
          getPlayEventParticipantDisplayName(participant),
        ])
      ),
    [eligibleParticipants]
  );

  useEffect(() => {
    if (!open) {
      setStep(1);
      setPreview(null);
      setPreviewTab('round');
      setSelectedRoundNumber(1);
      setAllocationMode('auto');
      setRoundCount(String(event.roundCount ?? 4));
      setRoundDurationMinutes(String(event.roundDurationMinutes ?? 30));
      setRoundBreakMinutes(String(event.roundBreakMinutes ?? 0));
      setPairingPriorities(event.pairingPriorities ?? DEFAULT_PAIRING_PRIORITIES);
      setOddPlayerPolicy(event.oddPlayerPolicy ?? 'sit_out');
      setCourtAssignmentsByKey({});
      setPairingShuffleSeed(0);
    }
  }, [open, event]);

  useEffect(() => {
    if (!open || !event.clubId) return undefined;
    let cancelled = false;
    void clubService.getClubCourts(event.clubId).then((res) => {
      if (cancelled || !res.success || !res.data) return;
      setCourtNamesById(Object.fromEntries(res.data.map((court) => [court.id, court.name])));
    });
    return () => {
      cancelled = true;
    };
  }, [open, event.clubId]);

  const previewInput = useMemo(
    () => ({
      event,
      participants,
      allocationMode,
      roundCount: Number(roundCount) || event.roundCount || 4,
      roundDurationMinutes: Number(roundDurationMinutes) || event.roundDurationMinutes || 30,
      roundBreakMinutes: Number(roundBreakMinutes) || event.roundBreakMinutes || 0,
      pairingPriorities,
      oddPlayerPolicy,
      pairingShuffleSeed,
    }),
    [
      event,
      participants,
      allocationMode,
      roundCount,
      roundDurationMinutes,
      roundBreakMinutes,
      pairingPriorities,
      oddPlayerPolicy,
      pairingShuffleSeed,
    ]
  );

  const buildPreview = (shuffleSeed = pairingShuffleSeed) =>
    buildEventDrawPreview({ ...previewInput, pairingShuffleSeed: shuffleSeed });

  const applyPreviewCourtAssignments = (nextPreview: DrawPreview) => {
    if (allocationMode === 'auto') {
      setCourtAssignmentsByKey(buildAutoCourtAssignmentsForPreview(nextPreview, event.courtIds));
      return;
    }
    setCourtAssignmentsByKey({});
  };

  const handleNextFromOptions = () => {
    if (eligibleParticipants.length < (event.format === 'single_elimination' ? 1 : 2)) {
      toast.error(
        event.format === 'social_rotate'
          ? 'At least two checked-in players are required'
          : 'At least two eligible players are required'
      );
      return;
    }
    setPairingShuffleSeed(0);
    const nextPreview = buildPreview(0);
    setPreview(nextPreview);
    applyPreviewCourtAssignments(nextPreview);
    setSelectedRoundNumber(nextPreview.rounds[0]?.roundNumber ?? 1);
    setStep(2);
  };

  const handleRegeneratePreview = () => {
    const nextSeed = pairingShuffleSeed + 1;
    setPairingShuffleSeed(nextSeed);
    const nextPreview = buildPreview(nextSeed);
    setPreview(nextPreview);
    applyPreviewCourtAssignments(nextPreview);
    toast.success('New auto draw generated');
  };

  const selectedPreviewRound = preview ? getPreviewRound(preview, selectedRoundNumber) : undefined;

  const selectedRoundMatches: DrawMatchListItem[] = useMemo(() => {
    if (!preview || !selectedPreviewRound) return [];
    return buildPreviewDrawMatchListItems({
      preview,
      event,
      participantNamesById,
      courtAssignmentsByKey,
      roundNumber: selectedRoundNumber,
    });
  }, [
    courtAssignmentsByKey,
    event,
    participantNamesById,
    preview,
    selectedPreviewRound,
    selectedRoundNumber,
  ]);

  const sittingOutParticipants = useMemo(() => {
    if (!selectedPreviewRound) return [];
    return selectedPreviewRound.sittingOut
      .map((id) => participantsById.get(id))
      .filter((participant): participant is PlayEventParticipant => Boolean(participant));
  }, [participantsById, selectedPreviewRound]);

  const eligibleParticipantIds = useMemo(
    () => eligibleParticipants.map((participant) => participant.id),
    [eligibleParticipants]
  );

  const availableParticipantsForRound = useMemo(() => {
    if (!preview) return [];
    return getAvailableParticipantsForPreviewRound(
      preview,
      selectedRoundNumber,
      eligibleParticipantIds
    )
      .map((id) => participantsById.get(id))
      .filter((participant): participant is PlayEventParticipant => Boolean(participant));
  }, [eligibleParticipantIds, participantsById, preview, selectedRoundNumber]);

  const availableParticipantOptions = useMemo(
    () =>
      availableParticipantsForRound.map((participant) => ({
        id: participant.id,
        displayName: getPlayEventParticipantDisplayName(participant),
      })),
    [availableParticipantsForRound]
  );

  const assignParticipantToPreviewSlot = (
    participantId: string,
    matchKey: string,
    side: 'a' | 'b',
    slotIndex = 0
  ) => {
    if (!preview) return;
    const matchIndex = Number(matchKey.split('-').pop());
    if (!Number.isFinite(matchIndex)) return;
    setPreview(
      assignPreviewParticipantToSlot(
        preview,
        selectedRoundNumber,
        matchIndex,
        side,
        participantId,
        slotIndex,
        resolvePlayEventMatchSideCapacity(event)
      )
    );
  };

  const handlePreviewDragEnd = (dragEvent: DragEndEvent) => {
    setDraggingParticipantId(null);
    if (!preview) return;

    playEventDrawMatchListDragHandler(dragEvent, {
      onSwap: (sourceParticipantId, targetParticipantId) => {
        setPreview(swapPreviewParticipants(preview, selectedRoundNumber, sourceParticipantId, targetParticipantId));
      },
      onAssignEmpty: (sourceParticipantId, matchKey, side, slotIndex) => {
        assignParticipantToPreviewSlot(sourceParticipantId, matchKey, side, slotIndex);
      },
    });
  };

  const handleSubmit = async () => {
    if (!preview) return;
    setSubmitting(true);
    try {
      const payload = buildDrawGeneratePayload(previewInput, preview, courtAssignmentsByKey);
      const res =
        event.format === 'social_rotate'
          ? await playEventService.startEvent(organizationId, eventId, payload)
          : await playEventService.generateRounds(organizationId, eventId, event.format, payload);

      if (res.success) {
        toast.success(event.format === 'social_rotate' ? 'Event started' : 'Draw generated');
        onOpenChange(false);
        onCompleted();
      } else {
        toast.error(res.error ?? 'Failed to generate draw');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const submitLabel =
    event.format === 'social_rotate' ? 'Start event' : 'Generate draw';

  return (
    <Drawer
      isOpen={open}
      onClose={() => onOpenChange(false)}
      title="Set up draw"
      description="Configure rounds, pairing rules, and preview matchups before generating the schedule."
      width="3xl"
      footer={
        <div className="flex w-full items-center justify-between gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              if (step === 1) {
                onOpenChange(false);
              } else {
                setStep((current) => (current - 1) as WizardStep);
              }
            }}
          >
            {step === 1 ? 'Cancel' : 'Back'}
          </Button>
          <div className="flex gap-2">
            {step < 3 ? (
              <Button
                type="button"
                onClick={() => {
                  if (step === 1) {
                    handleNextFromOptions();
                  } else {
                    setStep(3);
                  }
                }}
              >
                {step === 1 ? 'Preview draw' : 'Review'}
              </Button>
            ) : (
              <Button type="button" isLoading={submitting} onClick={() => void handleSubmit()}>
                <Wand2 className="mr-2 h-4 w-4" aria-hidden />
                {submitLabel}
              </Button>
            )}
          </div>
        </div>
      }
    >
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="border-b border-border pb-3">
          <ol className="flex items-center gap-2 text-sm">
            {[
              { id: 1, label: 'Options' },
              { id: 2, label: 'Preview' },
              { id: 3, label: 'Confirm' },
            ].map((wizardStep) => (
              <li
                key={wizardStep.id}
                className={cn(
                  'rounded-full px-3 py-1',
                  step === wizardStep.id
                    ? 'bg-primary-600 text-white'
                    : 'bg-muted text-muted-foreground'
                )}
              >
                {wizardStep.label}
              </li>
            ))}
          </ol>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto py-4">
          {step === 1 ? (
            <div className="space-y-6">
              <Field>
                <FieldLabel>Allocation mode</FieldLabel>
                <FieldDescription>
                  Auto uses your pairing rules to place players. Manual creates empty slots for you
                  to fill in the preview step.
                </FieldDescription>
                <Select
                  value={allocationMode}
                  onValueChange={(value) => setAllocationMode(value as DrawAllocationMode)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto allocate</SelectItem>
                    <SelectItem value="manual">Manual placement</SelectItem>
                  </SelectContent>
                </Select>
              </Field>

              {event.format === 'social_rotate' ? (
                <div className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-3">
                    <Field>
                      <FieldLabel>Rounds</FieldLabel>
                      <Input
                        type="number"
                        min={1}
                        max={50}
                        value={roundCount}
                        onChange={(changeEvent) => setRoundCount(changeEvent.target.value)}
                      />
                    </Field>
                    <Field>
                      <FieldLabel>Round duration (min)</FieldLabel>
                      <Input
                        type="number"
                        min={5}
                        max={240}
                        step={5}
                        value={roundDurationMinutes}
                        onChange={(changeEvent) =>
                          setRoundDurationMinutes(changeEvent.target.value)
                        }
                      />
                    </Field>
                    <Field>
                      <FieldLabel>Break between rounds (min)</FieldLabel>
                      <Input
                        type="number"
                        min={0}
                        max={120}
                        step={1}
                        value={roundBreakMinutes}
                        onChange={(changeEvent) => setRoundBreakMinutes(changeEvent.target.value)}
                      />
                    </Field>
                  </div>
                  {allocationMode === 'auto' ? (
                    <>
                      <Field>
                        <FieldLabel>Odd players</FieldLabel>
                        <Select
                          value={oddPlayerPolicy}
                          onValueChange={(value) => setOddPlayerPolicy(value as OddPlayerPolicy)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ODD_PLAYER_POLICIES.map((policy) => (
                              <SelectItem key={policy} value={policy}>
                                {ODD_PLAYER_POLICY_LABELS[policy]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>
                      <PlayEventPairingPrioritiesField
                        value={pairingPriorities}
                        onChange={setPairingPriorities}
                      />
                    </>
                  ) : null}
                </div>
              ) : null}

              {event.format === 'single_elimination' ? (
                <div className="rounded-lg border border-border bg-muted/20 p-4 text-sm text-muted-foreground">
                  Seeding uses player ratings when auto mode is selected. Switch to manual to place
                  players yourself in the preview step.
                </div>
              ) : null}
            </div>
          ) : null}

          {step === 2 && preview ? (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={previewTab === 'round' ? 'default' : 'outline'}
                    onClick={() => setPreviewTab('round')}
                  >
                    By round
                  </Button>
                  {event.format === 'single_elimination' ? (
                    <Button
                      type="button"
                      size="sm"
                      variant={previewTab === 'bracket' ? 'default' : 'outline'}
                      onClick={() => setPreviewTab('bracket')}
                    >
                      Bracket
                    </Button>
                  ) : null}
                </div>
                {allocationMode === 'auto' ? (
                  <Button type="button" size="sm" variant="outline" onClick={handleRegeneratePreview}>
                    <RefreshCw className="mr-2 h-4 w-4" aria-hidden />
                    Re-run auto
                  </Button>
                ) : null}
              </div>

              {allocationMode === 'manual' ? (
                <p className="text-sm text-muted-foreground">
                  Use the Add player dropdown on each empty slot, or drag players between filled
                  positions to swap them.
                </p>
              ) : null}

              {previewTab === 'bracket' && event.format === 'single_elimination' ? (
                <PlayEventBracketTree
                  matches={flattenDrawPreviewMatches(preview)}
                  participantNamesById={participantNamesById}
                />
              ) : (
                <>
                  <div className="flex gap-2 overflow-x-auto">
                    {preview.rounds.map((round) => (
                      <button
                        key={round.roundNumber}
                        type="button"
                        onClick={() => setSelectedRoundNumber(round.roundNumber)}
                        className={cn(
                          'shrink-0 rounded-full border px-3 py-1 text-sm',
                          selectedRoundNumber === round.roundNumber
                            ? 'border-primary-500 bg-primary-600 text-white'
                            : 'border-border bg-background text-muted-foreground'
                        )}
                      >
                        Round {round.roundNumber}
                      </button>
                    ))}
                  </div>

                  <DndContext
                    sensors={sensors}
                    collisionDetection={pointerWithin}
                    onDragStart={(dragEvent) => setDraggingParticipantId(String(dragEvent.active.id))}
                    onDragCancel={() => setDraggingParticipantId(null)}
                    onDragEnd={handlePreviewDragEnd}
                  >
                    <PlayEventDrawMatchList
                      event={event}
                      organizationId={organizationId}
                      eventId={eventId}
                      matches={selectedRoundMatches}
                      participantsById={participantsById}
                      courtNamesById={courtNamesById}
                      ratingSettings={null}
                      editable
                      draggingParticipantId={draggingParticipantId}
                      sittingOutParticipants={sittingOutParticipants}
                      unevenPanelTitle={
                        event.format === 'social_rotate' ? 'Sitting out' : 'Unassigned this round'
                      }
                      availableParticipantsForSlots={
                        event.format === 'single_elimination' &&
                        selectedPreviewRound.roundNumber > 1
                          ? []
                          : availableParticipantOptions
                      }
                      onAssignParticipantToSlot={
                        event.format === 'single_elimination' &&
                        selectedPreviewRound.roundNumber > 1
                          ? undefined
                          : assignParticipantToPreviewSlot
                      }
                      onCourtChange={(matchKey, courtId) => {
                        const assignmentKey = matchKey.startsWith('preview-')
                          ? matchKey.slice('preview-'.length)
                          : matchKey;
                        setCourtAssignmentsByKey((current) => ({
                          ...current,
                          [assignmentKey]: courtId,
                        }));
                      }}
                    />
                    <DragOverlay dropAnimation={null} modifiers={[snapCenterToCursor]}>
                      {draggingParticipantId && participantsById.get(draggingParticipantId) ? (
                        <PlayEventDrawPlayerDragGhost
                          participant={participantsById.get(draggingParticipantId)!}
                          event={event}
                          ratingSettings={null}
                        />
                      ) : null}
                    </DragOverlay>
                  </DndContext>

                  {allocationMode === 'auto' && preview.unassignedParticipantIds.length > 0 ? (
                    <div className="rounded-lg border border-dashed border-amber-300 bg-amber-50/50 p-3 text-sm dark:border-amber-700 dark:bg-amber-950/20">
                      <p className="font-medium text-foreground">Players without a match</p>
                      <p className="mt-1 text-muted-foreground">
                        {preview.unassignedParticipantIds
                          .map((id) => participantsById.get(id))
                          .filter((participant): participant is PlayEventParticipant =>
                            Boolean(participant)
                          )
                          .map((participant) => getPlayEventParticipantDisplayName(participant))
                          .join(', ')}
                      </p>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          ) : null}

          {step === 3 && preview ? (
            <PlayEventDrawReviewDiagram
              event={event}
              preview={preview}
              allocationMode={allocationMode}
              participantsById={participantsById}
              participantNamesById={participantNamesById}
              courtNamesById={courtNamesById}
              courtAssignmentsByKey={courtAssignmentsByKey}
            />
          ) : null}
        </div>
      </div>
    </Drawer>
  );
}
